import React from 'react';
import { Filter, X, Tag, Building2, Clock } from 'lucide-react';

export const AdvancedFilters = ({
  filters, setFilters, filterOptions, tagStatus,
  isDarkMode, onFetchResults
}) => {
  const inputClass = `w-full text-sm border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-demoraes-green/30 focus:border-demoraes-green transition-colors ${
    isDarkMode ? 'bg-zinc-800 border-zinc-700 text-zinc-200' : 'bg-white border-zinc-300 text-zinc-700'
  }`;

  const labelClass = `text-xs font-semibold uppercase tracking-wider mb-1.5 block ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`;

  const activeCount = [
    filters.tag ? 1 : 0,
    (filters.departments?.length || 0),
    (filters.company_times?.length || 0)
  ].reduce((a, b) => a + b, 0);

  const clearAll = () => {
    setFilters({ tag: '', departments: [], company_times: [] });
    setTimeout(() => onFetchResults(), 100);
  };

  const toggleArrayFilter = (field, value) => {
    const current = filters[field] || [];
    const updated = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    setFilters(prev => ({ ...prev, [field]: updated }));
  };

  const selectedTag = tagStatus?.find(t => t.tag === filters.tag);

  return (
    <div className={`rounded-lg border p-5 ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}
      data-testid="advanced-filters">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-demoraes-green" />
          <h3 className={`text-sm font-bold ${isDarkMode ? 'text-zinc-200' : 'text-zinc-800'}`}
            style={{ fontFamily: 'Manrope, sans-serif' }}>
            Filtros
          </h3>
          {activeCount > 0 && (
            <span className="bg-demoraes-green text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
              {activeCount}
            </span>
          )}
        </div>
        {activeCount > 0 && (
          <button onClick={clearAll}
            className="text-xs text-red-500 hover:text-red-400 flex items-center gap-1 transition-colors"
            data-testid="clear-filters">
            <X size={12} /> Limpar
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Tag / Client */}
        <div>
          <label className={labelClass}>
            <Tag size={11} className="inline mr-1" />
            Empresa / Tag
          </label>
          <select
            value={filters.tag || ''}
            onChange={(e) => {
              setFilters(prev => ({ ...prev, tag: e.target.value }));
            }}
            className={inputClass}
            data-testid="filter-tag"
          >
            <option value="">Todas as empresas</option>
            {tagStatus?.map(t => (
              <option key={t.tag} value={t.tag}>
                {t.client_name || t.tag} ({t.current_responses}/{t.employee_count})
              </option>
            ))}
            {/* Also include tags from filterOptions not in tagStatus */}
            {filterOptions?.tags?.filter(t => !tagStatus?.find(ts => ts.tag === t)).map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          {selectedTag && (
            <div className={`mt-1.5 text-xs flex items-center gap-2 ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>
              <span>{selectedTag.round_label || selectedTag.tag}</span>
              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                selectedTag.is_full
                  ? 'bg-demoraes-green/10 text-demoraes-green'
                  : isDarkMode ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-50 text-amber-700'
              }`}>
                {selectedTag.percentage}%
              </span>
              {selectedTag.report_emitted && (
                <span
                  className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                    isDarkMode ? 'bg-sky-900/30 text-sky-400' : 'bg-sky-50 text-sky-700'
                  }`}
                  title={selectedTag.last_emission_at
                    ? `Último relatório emitido em ${new Date(selectedTag.last_emission_at).toLocaleDateString('pt-BR')}`
                    : 'Relatório já emitido'}
                  data-testid="report-emitted-badge"
                >
                  Relatório emitido
                </span>
              )}
            </div>
          )}
        </div>

        {/* Departments */}
        <div>
          <label className={labelClass}>
            <Building2 size={11} className="inline mr-1" />
            Departamento
          </label>
          <div className={`max-h-32 overflow-y-auto rounded-md border p-2 space-y-1 ${
            isDarkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-zinc-300'
          }`}>
            {filterOptions?.departments?.length > 0 ? filterOptions.departments.map(dept => (
              <label key={dept} className="flex items-center gap-2 cursor-pointer py-0.5">
                <input
                  type="checkbox"
                  checked={filters.departments?.includes(dept)}
                  onChange={() => toggleArrayFilter('departments', dept)}
                  className="rounded border-zinc-400 w-3.5 h-3.5 text-demoraes-green focus:ring-demoraes-green"
                />
                <span className={`text-xs ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>{dept}</span>
              </label>
            )) : (
              <p className={`text-xs ${isDarkMode ? 'text-zinc-600' : 'text-zinc-400'}`}>Nenhum departamento</p>
            )}
          </div>
        </div>

        {/* Company time */}
        <div>
          <label className={labelClass}>
            <Clock size={11} className="inline mr-1" />
            Tempo de Empresa
          </label>
          <div className={`max-h-32 overflow-y-auto rounded-md border p-2 space-y-1 ${
            isDarkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-zinc-300'
          }`}>
            {filterOptions?.company_times?.length > 0 ? filterOptions.company_times.map(ct => (
              <label key={ct} className="flex items-center gap-2 cursor-pointer py-0.5">
                <input
                  type="checkbox"
                  checked={filters.company_times?.includes(ct)}
                  onChange={() => toggleArrayFilter('company_times', ct)}
                  className="rounded border-zinc-400 w-3.5 h-3.5 text-demoraes-green focus:ring-demoraes-green"
                />
                <span className={`text-xs ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>{ct}</span>
              </label>
            )) : (
              <p className={`text-xs ${isDarkMode ? 'text-zinc-600' : 'text-zinc-400'}`}>Nenhum período</p>
            )}
          </div>
        </div>
      </div>

      <button
        onClick={onFetchResults}
        className="mt-4 bg-demoraes-green hover:bg-demoraes-green/90 text-white text-sm font-semibold px-6 py-2 rounded-md transition-all"
        data-testid="apply-filters-btn"
      >
        Aplicar Filtros
      </button>
    </div>
  );
};
